import { useTranslation } from 'react-i18next'

import { PageContentBoundary } from '@/components/layout/page-content-boundary'
import { cn } from '@/lib/cn'

export type IdpClientDetailTab = 'applications' | 'clientScopes' | 'credentials' | 'settings'

export function IdpClientTabs({
  activeTab,
  contentClassName,
  embedded = false,
  onTabChange,
  showApplications = true,
}: {
  activeTab: IdpClientDetailTab
  contentClassName?: string
  embedded?: boolean
  onTabChange: (tab: IdpClientDetailTab) => void
  showApplications?: boolean
}) {
  const { t } = useTranslation('adminIdp')
  const tabs = (
    [
      { id: 'settings', label: t('clients.tabs.settings') },
      { id: 'credentials', label: t('clients.tabs.credentials') },
      { id: 'clientScopes', label: t('clients.tabs.clientScopes') },
      { id: 'applications', label: t('clients.tabs.applications') },
    ] satisfies { id: IdpClientDetailTab; label: string }[]
  ).filter((tab) => showApplications || tab.id !== 'applications')
  const tabList = (
    <div
      aria-label={t('clients.tabs.label')}
      className="flex h-12 min-w-0 gap-8 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      role="tablist"
    >
      {tabs.map((tab) => {
        const selected = activeTab === tab.id
        return (
          <button
            aria-controls={`oidc-client-${tab.id}-panel`}
            aria-selected={selected}
            className={cn(
              'h-12 shrink-0 cursor-pointer border-b-2 px-1 text-sm font-medium transition-colors',
              'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[var(--color-focus)]',
              selected
                ? 'border-[var(--color-primary)] font-semibold text-[var(--color-text)]'
                : 'border-transparent text-[var(--color-text-muted)] hover:text-[var(--color-text)]',
            )}
            id={`oidc-client-${tab.id}-tab`}
            key={tab.id}
            onClick={() => onTabChange(tab.id)}
            role="tab"
            type="button"
          >
            {tab.label}
          </button>
        )
      })}
    </div>
  )

  return (
    <div
      className={cn('relative z-20 shrink-0 bg-white', embedded ? '' : 'px-5 pb-3 lg:px-[30px]')}
    >
      {embedded ? (
        tabList
      ) : (
        <PageContentBoundary className={contentClassName}>{tabList}</PageContentBoundary>
      )}
    </div>
  )
}
